import * as React from 'react'
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form'
import { EndlessHolidayCreateFormInputs } from '@modules/product-package/transactions/create/modal'
import { ButtonWithIcon } from '@components/button-with-icon'
import {
  ProductPackagesAvailableAccommodationTypeOptions,
  EndlessHolidayFormProductDataRow,
  ProductPackageAdditionalOptions,
} from '@modules/product-package/transactions/create/steps/products/product-row'
import classNames from 'classnames'
import { useProductsAppData } from '@modules/products/hooks/use-products-app-data'
import { useGetProductPackageSetDetailsQuery } from '@api/endless-holiday'
import { LoaderPlaceholder } from '@components/loader-placeholder'
import { TypedQueryResult } from '@api/base'
import { ProductPackageKind, ProductPackageSetDetails, ProductPackageSetProduct } from '@modules/product-package/models'
import { createSelectOption, getById } from '@helpers/utils'
import { CustomReactSelectOption } from '@components/custom-react-select'
import { FormPlainError } from '@hyper/forms'

interface Props {
  productPackageKind: ProductPackageKind
}

export const EndlessHolidayFormProductData = ({ productPackageKind }: Props) => {
  const { product_package_sets } = useProductsAppData()
  const { control, getValues } = useFormContext<EndlessHolidayCreateFormInputs>()

  const { fields, append, remove } = useFieldArray({ control, name: 'products' })

  const selectedProductPackageSet = useWatch({ control, name: 'product_package_set' })

  const productPackageSet = getById(product_package_sets, selectedProductPackageSet?.value)

  const { currentData: packageSetDetails } = useGetProductPackageSetDetailsQuery(productPackageSet?.urls.details, {
    skip: !productPackageSet,
  }) as TypedQueryResult<ProductPackageSetDetails>

  const singlePackage = productPackageKind === 'voucher' && !!productPackageSet?.is_winter

  const handleAddEmptyProduct = () => {
    if (!packageSetDetails?.products.length) return

    const firstAccommodationType = ProductPackagesAvailableAccommodationTypeOptions[0]

    const yearOptions = getYearsOptions(firstAccommodationType.value)

    append({
      gastro_card: gastroProducts.length ? gastroProducts[1].id.toString() : null,
      subscription: getSubscriptionProducts(
        yearOptions[0]?.value,
        firstAccommodationType.value.toString(),
      )[0].id.toString(),
      accommodation_type: firstAccommodationType,
      amount: 1,
      options: [ProductPackageAdditionalOptions[1]],
      year: yearOptions.length ? yearOptions[0] : null,
    })
  }

  const handleRemoveProduct = (index: number) => () => {
    remove(index)
  }

  const getYearsOptions = React.useCallback(
    (accommodationType: number) => {
      if (!packageSetDetails?.products.length) return []

      const productYears = packageSetDetails.products.reduce(
        (prev: CustomReactSelectOption<number, number>[], product: ProductPackageSetProduct) => {
          const years = product.stay_in_years
          if (!years) return prev

          const isAlreadyAdded = prev.some(year => year.value === years?.[0])

          if (!years?.length || isAlreadyAdded || product.accommodation_type !== accommodationType) return prev

          return [...prev, createSelectOption(years[0], years[0])]
        },
        [],
      )

      return productYears.sort((a, b) => a.value - b.value)
    },
    [packageSetDetails],
  )

  const gastroProducts = React.useMemo(
    () =>
      (packageSetDetails?.products || []).filter(
        product => product.kind === 'gastro_card_product_package_endless_holiday',
        [],
      ),
    [packageSetDetails],
  )

  const getSubscriptionProducts = (year: number | undefined, accommodationType: string) =>
    (packageSetDetails?.products || []).filter(
      product =>
        (!year || product.stay_in_years?.includes(year)) &&
        parseInt(accommodationType, 10) === product.accommodation_type,
    )

  React.useEffect(() => {
    if (singlePackage && packageSetDetails && !getValues('products').length) handleAddEmptyProduct()
  }, [singlePackage, packageSetDetails, fields.length])

  return (
    <LoaderPlaceholder content={productPackageSet}>
      <div className="px-3 pb-3">
        <h4 className="text-secondary fw-semi-bold">Wybrane produkty:</h4>
        {!fields.length && <p className="text-center">Żaden produkt nie został jeszcze dodany.</p>}
        {fields.map((product, index) => (
          <EndlessHolidayFormProductDataRow
            getYearOptions={getYearsOptions}
            gastroProducts={gastroProducts}
            className={classNames({ 'mt-2': index > 0 })}
            key={product.id}
            onRemove={handleRemoveProduct(index)}
            getSubscriptionProducts={getSubscriptionProducts}
            index={index}
            productPackageKind={productPackageKind}
            singlePackage={singlePackage}
          />
        ))}
        <FormPlainError name="products" className="mt-2" />
        {!singlePackage && (
          <ButtonWithIcon
            icon="uil-plus"
            text="Dodaj produkt"
            btnClass="btn col-3 mx-auto d-block mt-2 btn-tall"
            color="green"
            handleClick={handleAddEmptyProduct}
          />
        )}
      </div>
    </LoaderPlaceholder>
  )
}
